import React, { useMemo } from 'react';
import './PieChartWeight.css';

const PieChartWeight = ({ measurements, scheduleTargets }) => {
    const defaultTarget = { min: 66, max: 220 }; // From mockup

    const periods = useMemo(() => {
        if (scheduleTargets?.weightTimePeriod && scheduleTargets.weightTimePeriod.length > 0) {
            return scheduleTargets.weightTimePeriod.map(p => ({
                name: p.name,
                range: `${p.start_time} ~ ${p.end_time}`,
                target: p.target || defaultTarget
            }));
        }
        return [
            { name: 'Morning', range: '07:00 AM ~ 11:59 AM', target: defaultTarget },
            { name: 'Afternoon', range: '12:00 PM ~ 05:59 PM', target: defaultTarget },
            { name: 'Evening', range: '12:00 PM ~ 05:59 PM', target: defaultTarget } // Note mockup actually says "12:00 PM ~ 05:59 PM" for Evening as well, likely a typo in the mockup design, but we use standard
        ];
    }, [scheduleTargets]);


    const calculatePieData = (periodName, target) => {
        if (!measurements || measurements.length === 0) return { within: 0, above: 0, below: 0, total: 0 };

        const periodMeasurements = measurements.filter(m => {
            const mPeriod = m.period_name || m.period || 'Unknown';
            if (mPeriod.toLowerCase() === periodName.toLowerCase()) return true;

            if ((!m.period_name && !m.period) && m.measure_new_date_time) {
                const hour = parseInt(m.measure_new_date_time.split('T')[1].substring(0, 2));
                if (periodName === 'Morning' && hour >= 7 && hour < 12) return true;
                if (periodName === 'Afternoon' && hour >= 12 && hour < 18) return true;
                if (periodName === 'Evening' && (hour >= 18 || hour < 7)) return true;
            }
            return false;
        });

        let within = 0, above = 0, below = 0;

        periodMeasurements.forEach(m => {
            if (m.weight != null) {
                const val = parseFloat(m.weight);
                if (val < target.min) below++;
                else if (val > target.max) above++;
                else within++;
            }
        });

        const total = within + above + below;
        return { within, above, below, total };
    };

    const drawPieChart = (data) => {
        const radius = 50;
        const circumference = 2 * Math.PI * radius;

        if (data.total === 0) {
            return (
                <svg width="100%" height="100%" viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r={radius} fill="transparent" stroke="#e0e0e0" strokeWidth="20" />
                    <text x="60" y="65" textAnchor="middle" fontSize="12" fill="#999">No Data</text>
                </svg>
            );
        }

        const wPct = data.within / data.total;
        const aPct = data.above / data.total;
        const bPct = data.below / data.total;

        const wDash = wPct * circumference;
        const aDash = aPct * circumference;
        const bDash = bPct * circumference;

        const aOffset = -wDash;
        const bOffset = aOffset - aDash;

        return (
            <svg width="100%" height="100%" viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
                {wPct > 0 && (
                    <circle
                        cx="60" cy="60" r={radius}
                        fill="transparent" stroke="#79b6c4" strokeWidth="20"
                        strokeDasharray={`${wDash} ${circumference}`}
                    />
                )}
                {aPct > 0 && (
                    <circle
                        cx="60" cy="60" r={radius}
                        fill="transparent" stroke="#1c818f" strokeWidth="20"
                        strokeDasharray={`${aDash} ${circumference}`}
                        strokeDashoffset={aOffset}
                    />
                )}
                {bPct > 0 && (
                    <circle
                        cx="60" cy="60" r={radius}
                        fill="transparent" stroke="#6098a3" strokeWidth="20"
                        strokeDasharray={`${bDash} ${circumference}`}
                        strokeDashoffset={bOffset}
                    />
                )}

                {/* Simple text labels on top of SVG could be added, but typically tooltips or center text is better.
            The mockup shows percentages physically on the slices. Calculating middle of arcs mathematically is complex for raw SVG, 
            so we approximate or just show in legend. Since mockup has them on arcs:
            We will use CSS positions for simplicity if desired, or skip the exact text-on-slice for a cleaner look if complex.
            Given the instruction, we'll draw the arcs perfectly.
        */}
            </svg>
        );
    };

    const getPercentageSpan = (data, type) => {
        if (data.total === 0) return '0%';
        const pct = (data[type] / data.total) * 100;
        return `${Math.round(pct * 10) / 10}%`; // Round to 1 decimal
    };

    return (
        <div className="pcw-section">
            <h4 className="pcw-main-title">Pie Chart of Weight</h4>

            <div className="pcw-cards-container">
                {periods.map(period => {
                    const data = calculatePieData(period.name, period.target);

                    return (
                        <div className="pcw-card" key={period.name}>
                            <div className="pcw-card-title">
                                {period.name} <span className="pcw-period-desc">(Time : {period.range}, Target : {period.target.min} ~ {period.target.max})</span>
                            </div>

                            <div className="pcw-card-body">
                                <div className="pcw-chart-visual">
                                    {drawPieChart(data)}

                                    {/* Overlay percentages (Simplified positioning) */}
                                    {data.total > 0 && data.within > 0 && (
                                        <div className="pcw-pct-label pcw-pct-within">{getPercentageSpan(data, 'within')}</div>
                                    )}
                                    {data.total > 0 && data.above > 0 && (
                                        <div className="pcw-pct-label pcw-pct-above">{getPercentageSpan(data, 'above')}</div>
                                    )}
                                    {data.total > 0 && data.below > 0 && (
                                        <div className="pcw-pct-label pcw-pct-below">{getPercentageSpan(data, 'below')}</div>
                                    )}
                                </div>

                                <div className="pcw-legend">
                                    <div className="pcw-legend-item">
                                        <div className="pcw-legend-dot pcw-dot-within"></div>
                                        <span>Within Target</span>
                                    </div>
                                    <div className="pcw-legend-item">
                                        <div className="pcw-legend-dot pcw-dot-above"></div>
                                        <span>above</span>
                                    </div>
                                    <div className="pcw-legend-item">
                                        <div className="pcw-legend-dot pcw-dot-below"></div>
                                        <span>below</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default PieChartWeight;
